// Browser-side Cognito flows. Replaces the redirect-to-Hosted-UI dance
// with in-app forms for sign-up, confirm, sign-in, force-new-password,
// and forgot/reset. Tokens land in the same localStorage keys the rest
// of the app reads, so no downstream code changes.

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  type ISignUpResult,
} from "amazon-cognito-identity-js";

// Build-time configurable; deploy.py --frontend injects per-stack values
// from Pulumi outputs. Defaults below are the dev-stack values for local
// `pnpm dev:web`.
const USER_POOL_ID =
  import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "ap-southeast-2_Qu6BzEGxb";
const CLIENT_ID =
  import.meta.env.VITE_COGNITO_CLIENT_ID ?? "63rpfpibfvaq68qei7k41id1i5";

const TOKEN_KEY = "cognito_id_token";
const REFRESH_KEY = "cognito_refresh_token";

function pool(): CognitoUserPool {
  return new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID });
}

function userFor(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: pool() });
}

function persistSession(session: CognitoUserSession): void {
  const idToken = session.getIdToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();
  localStorage.setItem(TOKEN_KEY, idToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

// Map raw Cognito error objects to friendlier copy. Fallbacks to the
// verbatim `.message` since Cognito's messages are generally OK.
export function friendlyCognitoError(err: unknown): string {
  const e = err as { code?: string; name?: string; message?: string };
  const code = e.code ?? e.name ?? "";
  switch (code) {
    case "UserNotFoundException":
      return "No account exists for that email address.";
    case "NotAuthorizedException":
      return "Email or password is incorrect.";
    case "UserNotConfirmedException":
      return "Please confirm your email address first.";
    case "UsernameExistsException":
      return "An account with that email already exists.";
    case "InvalidPasswordException":
      return "Password does not meet the policy (min 8 chars, upper + lower + number).";
    case "CodeMismatchException":
      return "That verification code is incorrect.";
    case "ExpiredCodeException":
      return "That verification code has expired. Please request a new one.";
    case "LimitExceededException":
      return "Too many attempts. Try again in a few minutes.";
    case "InvalidParameterException":
      return e.message ?? "Invalid input.";
    default:
      return e.message ?? String(err);
  }
}

// ---------------- Sign up / confirm ----------------

export function signUp(
  email: string,
  password: string,
): Promise<ISignUpResult> {
  return new Promise((resolve, reject) => {
    pool().signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: "email", Value: email })],
      [],
      (err, result) => {
        if (err || !result) return reject(err ?? new Error("signUp returned no result"));
        resolve(result);
      },
    );
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userFor(email).confirmRegistration(code, true, (err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userFor(email).resendConfirmationCode((err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

// ---------------- Sign in ----------------

export type SignInResult =
  | { status: "ok" }
  | {
      status: "new_password_required";
      user: CognitoUser;
      userAttributes: Record<string, string>;
    };

export function signIn(email: string, password: string): Promise<SignInResult> {
  const user = userFor(email);
  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        persistSession(session);
        resolve({ status: "ok" });
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        // Cognito forbids re-sending `email` / `email_verified` on
        // completeNewPasswordChallenge; strip them here.
        const cleaned: Record<string, string> = {};
        for (const [k, v] of Object.entries(userAttributes ?? {})) {
          if (k !== "email" && k !== "email_verified") cleaned[k] = String(v);
        }
        resolve({ status: "new_password_required", user, userAttributes: cleaned });
      },
    });
  });
}

export function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string>,
): Promise<void> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => {
        persistSession(session);
        resolve();
      },
      onFailure: (err) => reject(err),
    });
  });
}

// ---------------- Forgot / reset ----------------

export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userFor(email).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: () => resolve(),
    });
  });
}

export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    userFor(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}
